import apiClient from 'core/api/client';
import type {
  OrgMember,
  OrgRole,
  OrgDriver,
  SetDriverVehiclesBody,
  UpdateMemberRoleBody,
} from 'types/api.generated';

export const listOrgRoles = async (): Promise<OrgRole[]> => {
  const { data } = await apiClient.get<OrgRole[]>('/api/v1/organizations/roles');
  return data;
};

export const listOrgDrivers = async (): Promise<OrgDriver[]> => {
  const { data } = await apiClient.get<OrgDriver[]>('/api/v1/organizations/drivers');
  return data;
};

/** Creates or restores a driver profile for an active org member. Requires vehicle.manage. */
export const enableOrgDriver = async (
  userId: string,
  orgId?: string,
): Promise<OrgDriver> => {
  const { data } = await apiClient.post<OrgDriver>(
    `/api/v1/organizations/drivers/${userId}`,
    undefined,
    orgId ? { headers: { 'X-Org-Id': orgId } } : undefined,
  );
  return data;
};

/** Archives driver profile; member unchanged. Requires vehicle.manage. */
export const disableOrgDriver = async (
  userId: string,
  orgId?: string,
): Promise<void> => {
  await apiClient.delete(`/api/v1/organizations/drivers/${userId}`, {
    ...(orgId ? { headers: { 'X-Org-Id': orgId } } : undefined),
  });
};

export const listOrgMembers = async (): Promise<OrgMember[]> => {
  const { data } = await apiClient.get<OrgMember[]>('/api/v1/organizations/members');
  return data;
};

/** Path param is the organization member row id (from list members), not user id. */
export const updateOrgMemberRole = async (
  memberId: string,
  body: UpdateMemberRoleBody,
  orgId?: string,
): Promise<OrgMember> => {
  const { data } = await apiClient.patch<OrgMember>(
    `/api/v1/organizations/members/${memberId}`,
    body,
    orgId ? { headers: { 'X-Org-Id': orgId } } : undefined,
  );
  return data;
};

/** Path param is the organization member row id (from list members), not user id. */
export const removeOrgMember = async (
  memberId: string,
  orgId?: string,
): Promise<void> => {
  await apiClient.delete(`/api/v1/organizations/members/${memberId}`, {
    ...(orgId ? { headers: { 'X-Org-Id': orgId } } : undefined),
  });
};

/**
 * Replace which org vehicles a driver may use when going online.
 * Path param must be the driver's **userId** (not driver profile id).
 * Requires vehicle.manage and X-Org-Id. Returns 204 on success.
 */
export const setDriverVehicleAccess = async (
  userId: string,
  body: SetDriverVehiclesBody,
  orgId?: string,
): Promise<void> => {
  await apiClient.put(
    `/api/v1/organizations/drivers/${userId}/vehicles`,
    body,
    orgId ? { headers: { 'X-Org-Id': orgId } } : undefined,
  );
};
